import { useEffect, useMemo, useRef } from "react";
import { Button } from "primereact/button";

import { CardTemplate } from "./CardTemplate";
import { useIntroHighlightedContent, type HighlightedContentCard } from "../../../../featureFlags";
import { useUserContext } from "sj-ui-toolkit/src/atlas/contexts/User/UserContextProvider";
import { useSegmentTrackEvent } from "sj-ui-toolkit/src/hooks";
import { hasAiBundle } from "sj-ui-toolkit/src/constants";

// LD provides the image filename, which we match to the corresponding image in our public/images folder
const addImageUrls = (items: HighlightedContentCard[]) =>
  items.map(item => (item.image ? { ...item, image: `images/${item.image}.png` } : item));

export const HighlightedContent = ({
  additionalItems = [],
}: {
  additionalItems?: HighlightedContentCard[];
}) => {
  const { isTourVisible, toggleTourVisibility, contentBundles } = useUserContext();
  const flagCardValues = useIntroHighlightedContent();
  const trackEvent = useSegmentTrackEvent();
  const hasSentTourViewed = useRef(false);
  const sortKeysRef = useRef(new Map<string | undefined, number>());

  const getSortKey = (cardId: string | undefined): number => {
    const existing = sortKeysRef.current.get(cardId);
    if (existing !== undefined) return existing;

    const sortKey = Math.random();
    sortKeysRef.current.set(cardId, sortKey);
    return sortKey;
  };

  // Derive cards from the latest bundles. While loading (contentBundles === null) we show no tour
  // cards rather than risk showing the wrong set (e.g. enterprise cards to an AI tenant).
  // On error the lookup yields empty bundles, so we fall back to the default (non-AI) cards.
  // Non-primary cards are sorted by a random key to avoid showing the same card first every time.
  const tourItems = useMemo(() => {
    if (!flagCardValues || contentBundles === null) return [];
    const bundles = Array.isArray(contentBundles) ? contentBundles : [];
    const sourceItems = hasAiBundle(bundles)
      ? (flagCardValues.aiOnlyItems ?? []).filter(item =>
          (item.showIf ?? []).some(b => bundles.includes(b))
        )
      : (flagCardValues.defaultItems ?? []);

    const itemsWithImages = addImageUrls(sourceItems);
    const primaryCards = itemsWithImages.filter(item => item.primaryCard);
    const remainingCards = itemsWithImages
      .filter(item => !item.primaryCard)
      .sort((a, b) => getSortKey(a.title) - getSortKey(b.title));
    return [...primaryCards, ...remainingCards];
  }, [flagCardValues, contentBundles]);

  const highlightedCardData = useMemo(() => {
    return isTourVisible ? [...tourItems, ...additionalItems] : [...additionalItems];
  }, [additionalItems, tourItems, isTourVisible]);

  useEffect(() => {
    if (isTourVisible && highlightedCardData.length && !hasSentTourViewed.current) {
      trackEvent("Tour viewed", {});
      hasSentTourViewed.current = true;
    }
  }, [isTourVisible, highlightedCardData, trackEvent]);

  if (!highlightedCardData.length || !isTourVisible) {
    return null;
  }
  return (
    <div className="pb-5" data-testid="dashboard-highlighted-content">
      <div className="flex justify-end pb-1">
        <Button
          label="Dismiss"
          icon="pi pi-times"
          severity="secondary"
          text={true}
          data-testid="dashboard-highlighted-content-dismiss-button"
          onClick={toggleTourVisibility}
        />
      </div>
      <div className="horizontalCardScrollContainer">
        {highlightedCardData.map((item, index) => (
          <CardTemplate {...item} key={item.id || index} />
        ))}
      </div>
    </div>
  );
};
